import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CommissionRuleItem, CommissionRuleListQuery } from '@crm/shared';
import { extractApiError } from '../../lib/api';
import { useUserDirectory } from '../schedules/api';
import { useServicesList } from '../services/api';
import {
  useCommissionRuleDelete,
  useCommissionRules,
} from './api';
import { CommissionRuleModal } from './CommissionRuleModal';

export function CommissionRulesPage(): ReactNode {
  const { t } = useTranslation();

  const [userFilter, setUserFilter] = useState<string>('');
  const [serviceFilter, setServiceFilter] = useState<string>('');
  const [activeFilter, setActiveFilter] =
    useState<CommissionRuleListQuery['active']>('all');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CommissionRuleItem | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const filter: CommissionRuleListQuery = useMemo(
    () => ({
      userId: userFilter ? Number(userFilter) : undefined,
      serviceId: serviceFilter ? Number(serviceFilter) : undefined,
      active: activeFilter,
    }),
    [userFilter, serviceFilter, activeFilter],
  );

  const { data: rules = [], isLoading, error } = useCommissionRules(filter);
  const employeesQuery = useUserDirectory();
  const servicesQuery = useServicesList({ active: 'true' });
  const del = useCommissionRuleDelete();

  const employees = useMemo(
    () => (employeesQuery.data ?? []).filter((u) => u.bookable && u.active),
    [employeesQuery.data],
  );
  const services = useMemo(() => servicesQuery.data ?? [], [servicesQuery.data]);

  const onCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const onEdit = (rule: CommissionRuleItem) => {
    setEditing(rule);
    setModalOpen(true);
  };

  const onDelete = async (rule: CommissionRuleItem) => {
    setActionError(null);
    const label = rule.service ? rule.service.name : t('commissions.rules.baseLabel');
    if (
      !window.confirm(
        t('commissions.rules.deleteConfirm', {
          employee: `${rule.user.firstName} ${rule.user.lastName}`,
          scope: label,
        }),
      )
    ) {
      return;
    }
    try {
      await del.mutateAsync(rule.id);
    } catch (err) {
      setActionError(extractApiError(err));
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(`${iso}T00:00:00`);
    return new Intl.DateTimeFormat('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(d);
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">
            {t('commissions.rules.title')}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            {t('commissions.rules.subtitle')}
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={onCreate}>
          {t('commissions.rules.actions.add')}
        </button>
      </header>

      {actionError && (
        <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-700">
          {actionError}
        </div>
      )}

      {/* Filtry */}
      <section className="flex flex-wrap items-center gap-3 rounded-xl bg-white p-4 shadow-sm ring-1 ring-zinc-200">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-zinc-500">{t('commissions.rules.filter.employee')}:</span>
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="input min-w-[180px]"
          >
            <option value="">{t('commissions.rules.filter.allEmployees')}</option>
            {employees.map((u) => (
              <option key={u.id} value={u.id}>
                {u.firstName} {u.lastName}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-zinc-500">{t('commissions.rules.filter.service')}:</span>
          <select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="input min-w-[180px]"
          >
            <option value="">{t('commissions.rules.filter.allServices')}</option>
            <option value="">{t('commissions.rules.baseLabel')}</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-zinc-500">{t('commissions.rules.filter.active')}:</span>
          {(['all', 'true', 'false'] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setActiveFilter(opt)}
              className={`rounded-md px-3 py-1.5 text-sm transition ${
                activeFilter === opt
                  ? 'bg-brand-100 text-brand-700'
                  : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
              }`}
            >
              {t(`commissions.rules.filter.activeOpt.${opt}`)}
            </button>
          ))}
        </div>
      </section>

      {/* Tabela */}
      <section className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-zinc-200">
        {isLoading && (
          <div className="p-6 text-sm text-zinc-500">{t('common.loading')}</div>
        )}
        {error && (
          <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-700">
            {extractApiError(error)}
          </div>
        )}
        {!isLoading && rules.length === 0 && (
          <div className="p-6 text-sm text-zinc-500">{t('commissions.rules.empty')}</div>
        )}
        {rules.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wide text-zinc-500">
                  <th className="py-2 pr-4">{t('commissions.rules.table.employee')}</th>
                  <th className="py-2 pr-4">{t('commissions.rules.table.scope')}</th>
                  <th className="py-2 pr-4 text-right">
                    {t('commissions.rules.table.percent')}
                  </th>
                  <th className="py-2 pr-4">{t('commissions.rules.table.validFrom')}</th>
                  <th className="py-2 pr-4">{t('commissions.rules.table.validTo')}</th>
                  <th className="py-2 pr-4 text-right">
                    {t('commissions.rules.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} className="border-b border-zinc-100 last:border-0">
                    <td className="py-2 pr-4 font-medium text-zinc-900">
                      {r.user.firstName} {r.user.lastName}
                    </td>
                    <td className="py-2 pr-4">
                      {r.service ? (
                        <span>
                          {r.service.name}
                          {r.service.category && (
                            <span className="ml-1 text-xs text-zinc-400">
                              ({r.service.category})
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                          {t('commissions.rules.baseLabel')}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-right font-mono tabular-nums">
                      {r.percent}%
                    </td>
                    <td className="py-2 pr-4">{formatDate(r.validFrom)}</td>
                    <td className="py-2 pr-4">
                      {r.validTo ? (
                        formatDate(r.validTo)
                      ) : (
                        <span className="text-zinc-400">
                          {t('commissions.rules.openEnded')}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => onEdit(r)}
                          className="rounded-md px-2 py-1 text-xs text-brand-700 hover:bg-brand-50"
                        >
                          {t('common.actions.edit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(r)}
                          className="rounded-md px-2 py-1 text-xs text-rose-600 hover:bg-rose-50"
                        >
                          {t('common.actions.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <CommissionRuleModal
        open={modalOpen}
        rule={editing}
        employees={employees}
        services={services}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}
